/**
 * GenieX Experience — a cinematic top-down digital twin of a luxury villa.
 *
 * PRD summary (Aug 2026 refactor):
 *   • Living, Dining, Kitchen, Bedroom 1, Bedroom 2, 2 Bathrooms, Water Tank, Driveway/Gate.
 *   • Devices: lights (dimmable), fans, ACs (airflow), TVs, curtains, gate,
 *     smart-lock w/ face recognition, geyser, appliance, robot vacuum, doorbell,
 *     safety sensors.
 *   • 6 Scenes with orchestrated timelines: Good Morning, Good Night, Movie,
 *     Party, Away, Vacation.
 *   • Ambient daylight simulation (morning/day/evening/night).
 *   • Desktop: villa + floating GenieX Home phone dock, in sync.
 *   • Mobile: page transforms INTO the phone UI — no phone-in-phone.
 *   • Safety Engine: fire / gas / water leak — dedicated visual + notification.
 *
 * All villa/rooms/devices/phone-dock/scene-engine pieces now live in
 * `src/features/villaExperience/` as a standalone, reusable module — the goal
 * being that a future embed (e.g. a "see it in your home" widget on the Value
 * Packs page, showing only that pack's rooms/devices) can import the same
 * VillaCanvas / PhoneDock / buildScenes without duplicating this page.
 */

import { useReducer, useState, useEffect, useMemo, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles } from "lucide-react";
import { useSEO } from "../lib/seo";
import { initialState, reducer, buildScenes, VACUUM_ROUTE } from "../features/villaExperience/config";
import { AmbientBackground, VillaCanvas } from "../features/villaExperience/VillaCanvas";
import { PhoneDock } from "../features/villaExperience/PhoneDock";
import { DoorbellOverlay, SafetyOverlay } from "../features/villaExperience/Overlays";

export default function ExperiencePage() {
  useSEO({
    title: "GenieX Experience — Interactive Digital Twin",
    description:
      "Step inside a luxury smart villa. Tap lights, fans, AC, curtains, TV, gates and safety systems. Six curated scenes orchestrate the entire home in real time.",
  });

  const [state, dispatch] = useReducer(reducer, initialState);
  const [activeRoom, setActiveRoom] = useState("living");
  const [showHint, setShowHint] = useState(true);
  const [showFabHint, setShowFabHint] = useState(false);
  const idleTimer = useRef(null);
  const sceneTimers = useRef([]);
  const SCENES = useMemo(buildScenes, []);

  const markActivity = useCallback(() => {
    setShowFabHint(false);
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => setShowFabHint(true), 5000);
  }, []);

  const dispatchTracked = useCallback(
    (action) => {
      markActivity();
      dispatch(action);
    },
    [markActivity]
  );

  const setActiveRoomTracked = useCallback(
    (r) => {
      markActivity();
      setActiveRoom(r);
    },
    [markActivity]
  );

  const vacuumTimers = useRef([]);
  const startVacuum = useCallback(() => {
    markActivity();
    vacuumTimers.current.forEach((t) => clearTimeout(t));
    vacuumTimers.current = [];
    const perRoom = 1300;
    dispatch({ type: "PATCH_VACUUM", patch: { status: "cleaning", room: VACUUM_ROUTE[0], progress: 0, done: [] } });
    VACUUM_ROUTE.forEach((room, i) => {
      const t = setTimeout(() => {
        dispatch({
          type: "PATCH_VACUUM",
          patch: { room, progress: Math.round(((i + 1) / VACUUM_ROUTE.length) * 90), done: VACUUM_ROUTE.slice(0, i) },
        });
      }, i * perRoom);
      vacuumTimers.current.push(t);
    });
    const returnT = setTimeout(() => {
      dispatch({ type: "PATCH_VACUUM", patch: { status: "returning", room: null, progress: 95, done: VACUUM_ROUTE } });
      const dockT = setTimeout(() => dispatch({ type: "PATCH_VACUUM", patch: { status: "docked", progress: 100 } }), 1300);
      vacuumTimers.current.push(dockT);
    }, VACUUM_ROUTE.length * perRoom);
    vacuumTimers.current.push(returnT);
  }, [markActivity]);

  const runScene = useCallback(
    (sceneId) => {
      markActivity();
      sceneTimers.current.forEach((t) => clearTimeout(t));
      sceneTimers.current = [];
      dispatch({ type: "SCENE_START", scene: sceneId });
      const s = SCENES[sceneId];
      if (!s) return;
      s.steps.forEach((step) => {
        const t = setTimeout(() => step.do(dispatch), step.at);
        sceneTimers.current.push(t);
      });
      const totalMs = Math.max(...s.steps.map((s) => s.at)) + 800;
      const end = setTimeout(() => dispatch({ type: "SCENE_END" }), totalMs);
      sceneTimers.current.push(end);
      if (sceneId === "away") {
        const vacT = setTimeout(() => startVacuum(), totalMs + 400);
        sceneTimers.current.push(vacT);
      }
    },
    [SCENES, markActivity, startVacuum]
  );

  const triggerSafety = useCallback(
    (kind) => {
      markActivity();
      dispatch({ type: "SET", patch: { alarm: kind } });
      setTimeout(() => dispatch({ type: "SET", patch: { alarm: null } }), 5200);
    },
    [markActivity]
  );

  const triggerDoorbell = useCallback(() => {
    markActivity();
    dispatch({ type: "SET", patch: { doorbell: true } });
    setTimeout(() => dispatch({ type: "SET", patch: { doorbell: false } }), 4200);
  }, [markActivity]);

  useEffect(() => {
    if (!state.waterTank.motorOn) return;
    const i = setInterval(() => dispatch({ type: "TICK_WATER_TANK" }), 700);
    return () => clearInterval(i);
  }, [state.waterTank.motorOn]);

  useEffect(() => () => vacuumTimers.current.forEach((t) => clearTimeout(t)), []);

  useEffect(() => {
    if (
      activeRoom !== "living" ||
      Object.values(state.rooms).some((r) => r.light > 0 || r.ac || r.tv || r.fan > 0)
    ) {
      setShowHint(false);
    }
  }, [state, activeRoom]);

  useEffect(() => {
    markActivity();
    return () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [markActivity]);

  useEffect(() => () => sceneTimers.current.forEach((t) => clearTimeout(t)), []);

  return (
    <main
      data-testid="experience-page"
      className="relative min-h-screen w-full overflow-hidden text-white"
    >
      <AmbientBackground time={state.time} scene={state.scene} SCENES={SCENES} />

      <div className="relative z-20 pt-24 lg:pt-28 pb-6 px-5 lg:px-10 max-w-[1600px] mx-auto">
        <p className="eyebrow mb-3">GenieX Experience</p>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
          <h1 className="h1-display text-white leading-[1.05] tracking-tight">
            A living, breathing<br />
            <span className="text-gold">digital twin.</span>
          </h1>
          <p className="text-[13.5px] text-dim max-w-[46ch] lg:text-right">
            Tap a room. Toggle a device. Run a scene. Everything on screen —
            the villa above and the GenieX Home phone alongside — moves as one.
          </p>
        </div>
      </div>

      <div className="relative z-10 max-w-[1600px] mx-auto px-3 sm:px-5 lg:px-10 pb-24 lg:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_380px] gap-5 lg:gap-8">
          <div className="block">
            <VillaCanvas
              state={state}
              activeRoom={activeRoom}
              setActiveRoom={setActiveRoomTracked}
              onDoorbell={triggerDoorbell}
            />

            <div className="lg:hidden mt-5 rounded-[24px] border border-gold/15 bg-black/40 backdrop-blur-md p-4 shadow-[0_40px_80px_-40px_rgba(0,0,0,0.7)]">
              <PhoneDock
                state={state}
                dispatch={dispatchTracked}
                activeRoom={activeRoom}
                setActiveRoom={setActiveRoomTracked}
                runScene={runScene}
                triggerSafety={triggerSafety}
                triggerDoorbell={triggerDoorbell}
                SCENES={SCENES}
                startVacuum={startVacuum}
                embedded
              />
            </div>
          </div>

          <div className="hidden lg:block">
            <PhoneDock
              state={state}
              dispatch={dispatchTracked}
              activeRoom={activeRoom}
              setActiveRoom={setActiveRoomTracked}
              runScene={runScene}
              triggerSafety={triggerSafety}
              triggerDoorbell={triggerDoorbell}
              SCENES={SCENES}
              startVacuum={startVacuum}
            />
          </div>
        </div>
      </div>

      <AnimatePresence>
        {state.doorbell && <DoorbellOverlay key="db" onDismiss={() => dispatch({ type: "SET", patch: { doorbell: false } })} onUnlock={() => dispatch({ type: "SET", patch: { lock: "unlocked", doorbell: false } })} />}
        {state.alarm && <SafetyOverlay key="al" kind={state.alarm} />}
        {showHint && (
          <motion.div
            key="hint"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="hidden lg:block fixed bottom-6 left-1/2 -translate-x-1/2 z-40 rounded-full border border-gold/25 bg-black/60 backdrop-blur-xl px-5 py-2.5 text-[12px] tracking-[0.22em] uppercase text-amber-200/90"
            data-testid="experience-hint"
          >
            <Sparkles className="inline w-3.5 h-3.5 text-gold mr-2 -mt-0.5" />
            Tap a room to control it · Run a scene from the phone dock
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
